// Reusable demo content configs. Client-side only, no backend.
// Content is illustrative preview copy (not app screenshots).

export const demos = {
  playvia: {
    id: "playvia",
    name: "Playvia",
    logo: "/logos/playvia.png",
    accent: "#8B7DD8",
    value:
      "Screen-free activity ideas for moments when parents need inspiration quickly.",
    scenario: {
      question: "What kind of moment are you having today?",
      options: [
        {
          label: "Need something quick",
          activity: {
            title: "The 5-Minute Cloud Story",
            tag: "5 min · anywhere",
            steps: [
              "Look out a window together and find one cloud.",
              "Take turns saying what its shape reminds you of.",
              "Build a tiny story: where is it going, who does it meet?",
            ],
          },
        },
        {
          label: "Calm-down time",
          activity: {
            title: "Slow Breathing Balloon",
            tag: "3 min · calming",
            steps: [
              "Sit together and rest both hands on your tummy.",
              "Breathe in slowly and imagine a balloon gently filling.",
              "Breathe out longer than you breathed in — repeat five times.",
            ],
          },
        },
        {
          label: "Rainy day",
          activity: {
            title: "Indoor Treasure Hunt",
            tag: "10 min · indoor",
            steps: [
              "Pick three simple things to find around the room.",
              "Give one playful clue for each.",
              "Celebrate every find with a small cheer.",
            ],
          },
        },
        {
          label: "Learning through play",
          activity: {
            title: "Kitchen Sort & Count",
            tag: "8 min · learning",
            steps: [
              "Gather a handful of safe kitchen items.",
              "Sort them by size, then by colour.",
              "Count each group out loud together.",
            ],
          },
        },
      ],
    },
    valueMoment:
      "That's the Playvia rhythm — one clear idea, ready to do, in seconds.",
    previews: [
      {
        image: "/demo/playvia_demo_activity_1.jpg",
        title: "Move, Focus & Calm",
        label: "Mini Pack Vol. 1 · Ages 4–6 · Active",
        practices: "Body awareness, balance, and settling energy",
        note: "Short movement bursts first, then a calm finish — it helps active kids land gently.",
      },
      {
        image: "/demo/playvia_demo_activity_2.png",
        title: "Search, Think & Find",
        label: "Mini Pack Vol. 2 · Ages 4–6 · Discovery",
        practices: "Observation, focus, and early categorisation",
        note: "Bring one small basket — let your child decide what belongs together and why.",
      },
      {
        image: "/demo/playvia_demo_printable_1.png",
        title: "Colour & Imagine",
        label: "Printable Vol. 1 · Ages 4–6 · Creative",
        practices: "Fine motor control, focus, and imagination",
        note: "There's no wrong colour — ask what's happening in their picture as they go.",
      },
      {
        image: "/demo/playvia_demo_printable_2.png",
        title: "Colour & Imagine — Volume 2",
        label: "Printable Vol. 2 · Ages 4–6 · Creative",
        practices: "Colour choice, storytelling, and patience",
        note: "Print one page, not the whole pack — one finished picture is a win.",
      },
    ],
    howTo: {
      title: "How Playvia works",
      subtitle: "The quick-start guide every parent gets inside Playvia.",
      image: "/demo/playvia_how_to.png",
    },
    teaser: {
      copy: "More activities inside Playvia",
      cards: [
        { title: "Move, Focus & Calm", tag: "Mini Pack" },
        { title: "Backyard Sound Map", tag: "Outdoor" },
      ],
    },
    end: "More of the experience is coming soon.",
  },

  readywork: {
    id: "readywork",
    name: "ReadyWork",
    logo: "/logos/readywork.png",
    accent: "#2E7BC4",
    value:
      "See how ready you are for the role you want — and exactly what to strengthen next.",
    proofs: [
      {
        image: "/demo/readywork_demo_1.png",
        title: "Guided help, one tap away",
        caption:
          "Five focused tools — from drafting a CV to answering a recruiter — plus a quick vacancy safety check.",
      },
      {
        image: "/demo/readywork_demo_2.png",
        title: "Interview practice without memorising",
        caption:
          "Each question is broken into a flow to understand, not a script to memorise.",
      },
    ],
    flow: {
      label: "Demo scenario · Sample data",
      title: "Aiming at one vacancy: Marketing Executive",
      steps: [
        {
          title: "One target vacancy",
          body: "Pick a single role to aim at — here, a Marketing Executive opening at a growing local brand.",
        },
        {
          title: "What matches",
          body: "ReadyWork surfaces what already fits: two years running social campaigns, content calendar ownership, and basic ads reporting.",
        },
        {
          title: "What needs strengthening",
          body: "Two gaps become visible: no campaign budget ownership yet, and thin portfolio metrics.",
        },
        {
          title: "Next move",
          body: "Rewrite one CV bullet to lead with a measurable campaign result, and prepare a 60-second answer for the budget question.",
        },
      ],
    },
    result: {
      title: "One application, made sharper",
      body: "Instead of sending the same CV everywhere, this application now speaks directly to the Marketing Executive role — strengths matched up front, and a rehearsed answer ready for the obvious gap.",
    },
    valueMoment:
      "That's the ReadyWork difference — every application aimed at one real vacancy, not sprayed at fifty.",
    teaser: {
      copy: "There's more to prepare before you apply.",
      cards: [
        { title: "Vacancy Fit Check", tag: "Fit" },
        { title: "Recruiter Reply Draft", tag: "Communicate" },
      ],
    },
    end: "More of the experience is coming soon.",
  },

  profitpop: {
    id: "profitpop",
    name: "ProfitPop",
    logo: "/logos/profitpop.png",
    accent: "#2E9E77",
    value: "Understand your numbers before you change the price.",
    proofs: [
      {
        image: "/demo/profitpop_demo_1.png",
        title: "Start from a question, not a spreadsheet",
        caption:
          "Check profit, see what a change would impact, and revisit saved calculations — all from one calm home.",
      },
      {
        image: "/demo/profitpop_demo_2.png",
        title: "Speaks your kind of business",
        caption:
          "ProfitPop adjusts its terms and calculations to how you actually earn — products, services, content, or something else.",
      },
    ],
    flow: {
      label: "Demo scenario · Sample data",
      title: "Can I run a 15% discount without squeezing the margin too far?",
      steps: [
        {
          title: "Question",
          body: "A small food seller wants a weekend promo: 15% off, without quietly losing money on every cup.",
        },
        {
          title: "Numbers",
          body: "Sample data: iced coffee at Rp25.000, cost Rp14.000 per cup, about 90 cups sold a week.",
        },
        {
          title: "Understand",
          body: "The discount leaves Rp7.250 margin per cup — roughly a 34% margin instead of 44%.",
        },
        {
          title: "Decide",
          body: "Fine for a one-weekend promo to pull in new buyers — but not as an everyday price.",
        },
      ],
    },
    result: {
      title: "A decision you can explain",
      body: "Run the promo for the weekend only. Weekday margins stay healthy, and the discount works as a traffic push — not a slow leak.",
    },
    valueMoment:
      "That's the ProfitPop way — see the effect before you commit.",
    teaser: {
      copy: "See what changes before you change the price.",
      cards: [
        { title: "Impact of a Cost Increase", tag: "Understand" },
        { title: "Monthly Savings Target", tag: "Plan" },
      ],
    },
    end: "More of the experience is coming soon.",
  },

  skripsinext: {
    id: "skripsinext",
    name: "SkripsiNext",
    logo: "/logos/skripsinext.png",
    accent: "#4E8069",
    value:
      "Every piece of feedback, revision, and guidance — organized in one calm place.",
    proofs: [
      {
        image: "/demo/skripsinext_demo_1.png",
        title: "One inbox for every revision",
        caption:
          "Feedback is grouped by state — needs clarifying, in progress, ready for review — so nothing gets lost.",
      },
      {
        image: "/demo/skripsinext_demo_2.png",
        title: "Guidance sessions, prepared",
        caption:
          "Open questions, last session notes, and auto-compiled documents — ready before you meet your supervisor.",
      },
    ],
    flow: {
      label: "Demo scenario · Sample data",
      title: "From supervisor note to a clear next step",
      steps: [
        {
          title: "Supervisor feedback",
          body: "A new note arrives: “Make sure the methodology is aligned with Research Question 2.”",
        },
        {
          title: "Revision inbox",
          body: "It's filed under Chapter 3 — Methodology, marked as needs clarifying, next to the older items it relates to.",
        },
        {
          title: "Next move",
          body: "SkripsiNext turns it into a safe task: “Review whether the methodology aligns with Research Question 2.”",
        },
        {
          title: "Guidance prep",
          body: "The open question is queued for the next supervision session, with last session's notes already attached.",
        },
      ],
    },
    result: {
      title: "SkripsiNext organizes the work. The student owns the thinking.",
      body: "Nothing is written for the student — feedback becomes a clear, reviewable task list, so the next guidance session starts prepared instead of scrambled.",
    },
    valueMoment:
      "That's the SkripsiNext rhythm — no feedback lost, no session unprepared.",
    teaser: {
      copy: "Every revision, ready before the next guidance session.",
      cards: [
        { title: "Revision Matrix", tag: "Document" },
        { title: "Progress Summary", tag: "Document" },
      ],
    },
    end: "More of the experience is coming soon.",
  },

  uptoday: {
    id: "uptoday",
    name: "UpToday",
    logo: "/logos/uptoday.png",
    accent: "#6E8FD1",
    value:
      "A gentle daily reset companion — choose an Era, take one main step, and close the day feeling supported.",
    proofs: [
      {
        image: "/demo/uptoday_v2_today.png",
        title: "Today starts with one",
        caption:
          "Pick a single main step for the day — with gentle ideas if you're stuck — and optional supporting actions.",
      },
      {
        image: "/demo/uptoday_v2_profile.png",
        title: "Momentum you can feel",
        caption:
          "Active days, finished steps, and a companion that evolves with you — progress stays light and personal.",
      },
    ],
    flow: {
      label: "Demo scenario · Sample data",
      title: "One gentle day, from Era to evening close",
      steps: [
        {
          title: "Choose your era",
          body: "Set the theme of your current focus — like a Glow-Up Era — with one small goal inside it.",
        },
        {
          title: "Pick one main step",
          body: "Start small: one meaningful move for today, chosen in a single tap.",
        },
        {
          title: "Add supporting steps",
          body: "Break it into lighter actions — set a start time, prepare what you need.",
        },
        {
          title: "Stay encouraged",
          body: "Gentle ideas and soft visuals keep the day approachable, never pressured.",
        },
        {
          title: "Track your momentum",
          body: "See simple progress: main steps done, active days, and your companion evolving.",
        },
        {
          title: "Extend with planner tools",
          body: "Carry the day into a printable planner with sticker kits and Canva editing.",
        },
      ],
    },
    result: {
      title: "A day that feels finished, not endless",
      body: "One main step done, momentum counted, your companion a little further along — and tomorrow already has a gentle starting point.",
    },
    highlightsTitle: "What makes UpToday feel different",
    highlights: [
      "One main step first — less overwhelming than a long to-do list",
      "Era-based framing — focus feels personal, not generic",
      "A companion that grows with you — small delight, real warmth",
      "Planner + sticker kit — bridges the app and printable habits",
      "Gentle daily closure — the day ends on purpose",
    ],
    spotlight: {
      title: "Make the experience yours",
      body: "Pick a planner style, choose a companion, and add sticker kits — then edit in Canva or print it for the wall.",
      image: "/demo/uptoday_v2_planner.png",
    },
    valueMoment:
      "That's UpToday — a daily reset companion, not another task manager.",
    teaser: {
      copy: "Tomorrow starts with one good move.",
      cards: [
        { title: "Printable Day Planner", tag: "Planner" },
        { title: "Eras & Themes", tag: "Personal" },
      ],
    },
    end: "More of the experience is coming soon.",
  },
};

export const getDemo = (id) => demos[id];
